/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
type CardConfig,
    type CardTitleConfig, type ColumnDef, type Command, type JobResponse, type PromptConfigFull, type Rest, type TableConfig,
    AdvancedComponent, Browser, ButtonService, CAPACITY_UNIT, DataCardComponent, DMValidators,
    FormControl, FormGroup, LazyloadService, TimeService, UtilService,
} from '@dm/core';
import { type ButtonGroup, type SelectOption } from '@dm/core/types';
import { MO, SnapshotCreateModeE, SYSTEM_VSTORE_NAME } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { LicenseKey } from 'app/common/constants/license-key';
import { type HyperCdpSchedule, type Lun, type ProtectionLun, type SnapshotName, type SnapshotSendType } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { CreateComponent } from 'app/feature/protection/schedule/hyper-cdp/schedule/create/create.component';
import { objectTypeE } from 'app/feature/protection/schedule/hyper-cdp/schedule/create/create-str.service';
import { ScheduleService } from 'app/feature/protection/schedule/hyper-cdp/schedule/schedule.service';
import { ScheduleStrService } from 'app/feature/protection/schedule/hyper-cdp/schedule/schedule-str.service';
import { SIMPLE_CDP_SCHEDULE_SAN_DEFAULT } from 'app/feature/protection/simple-protection/simple-protection.const';
import { SystemVstoreId } from 'app/feature/system/hardware/device/detail/detail.str.service';
import { type Observable, of } from 'rxjs';

import { UNPLACEHOLDER } from '../../../../../../common/constants/index';
import { ProtectLunService } from '../../protect-lun/lun.service';
import { SnapshotService } from '../snapshot.service';
import { SnapshotStrService } from '../snapshot-str.service';
import { type CreateLunSnapFormValue } from './create-lun-snapshot-form';
import { SnapCreateDesLenE, SnapCreateNameLenE, SnapProtectTypeE, SnapSourceSelectTypeE } from './create-lun-snapshot-form.enum';

@Component({
    selector: 'app-create-lun-snapshot-form',
    templateUrl: './create-lun-snapshot-form.component.html',
    styleUrls: ['./create-lun-snapshot-form.component.scss'],
})
export class CreatLunSnapshotFormComponent implements OnInit {
    /**
     * 来源页面，影响联机帮助link地址
     */
    @Input() fromPage: 'lun' | 'protect_lun' | 'snapshot' = 'snapshot';

    /**
     * 单选or多选
     */
    @Input() selectType: SnapSourceSelectTypeE;

    /**
     * 已选中的lun
     */
    @Input() inputLuns: Lun[];

    /**
     * 租户
     */
    @Input() vstore: SelectOption;

    /**
     * 显示添加策略选项
     */
    @Input() showSchedule = false;

    /**
     * 显示单次快照选项
     */
    @Input() showSnap = true;

    /**
     * 是否为修改CDP策略场景
     */
    @Input() modifySchedule = false;

    /**
     * 关闭回调
     */
    @Output() onClose: EventEmitter<any> = new EventEmitter();

    /**
     * 可选LUN推拉表格ref
     */
    @ViewChild('selectedLunToggle') selectedLunToggle: AdvancedComponent;

    /**
     * 目标LUN高危提示table ref
     */
    @ViewChild('targetLunTemplate') targetLunTemplate: TemplateRef<any>;

    /**
     * 修改策略高危提示table ref
     */
    @ViewChild('modifyScheduleTableTemplate') modifyScheduleTableTemplate: TemplateRef<any>;

    /**
     * cdp策略卡片 ref
     */
    @ViewChild('scheduleDataCard') scheduleDataCard: DataCardComponent;

    /**
     * 常量定义
     */
    SWITCH = SWITCH;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;
    SnapSourceSelectTypeE = SnapSourceSelectTypeE;
    SnapshotCreateModeE = SnapshotCreateModeE;
    SnapProtectTypeE = SnapProtectTypeE;
    i18n = this.str.create;

    /**
     * 页面相关
     */
    pageReady = false;
    show = false;
    isRefresh = false;

    /**
     * 表格、表单
     */
    tableConfig: TableConfig;
    columnDefs: ColumnDef[];
    selectedLuns: Lun[] = [];
    selectedLunsChanged = false;
    vm: FormGroup;
    errorMessage;
    selectLunValid = false;

    /**
     * 策略的卡片表单
     */
    scheduleDefaultData = [];
    initSelectVal: string;
    dataCardConfig: CardConfig;
    scheduleBtnGroup: ButtonGroup;

    /**
     * 推拉表格
     */
    lunRest: Rest;
    selectedTargetLUN: SelectOption;
    targetLunColumnDefs: ColumnDef[];
    targetLunRest: Rest;

    modifyScheduleTableColumnDefs: ColumnDef[];

    /**
     * CDP策略rest
     */
    getBatchScheduleRest: Rest;

    /**
     * 名称placeholder，和长度有关
     */
    placeholderName: string;

    /**
     * 判断是否有远程复制，影响接口下发。有复制的情况不能走批量接口
     */
    hasRemoteReplication = false;

    /**
     * 联机帮助
     */
    helpKeyMap = {
        lun: 'lunCreateSnap',
        protect_lun: 'lunprotectionCreateSnap',
        snapshot: 'snapshotCreate',
    };

    /**
     * 快照时间戳后缀
     */
    timestamp = this.timeService.formatUTC(this.timeService.currTime * 1e3, 'YYMMDDHHmmss', false).split(' ')[0];

    /**
     * LUN推拉表格折叠标识
     */
    lunTableExpand = true;

    /**
     * 快照、策略切换卡片宽度
     */
    multiTitleWidth = '405px';

    /**
     * 策略卡片宽度
     */
    multiCardWidth = Browser.name === 'ie' ? '260px' : '265px';

    /**
     * 对form表单的值进行类型检查
     */
    get vmValue(): any {
        return this.vm.value as CreateLunSnapFormValue;
    }

    constructor(
        private scheduleService: ScheduleService,
        private scheduleStrService: ScheduleStrService,
        private str: SnapshotStrService,
        private utilService: UtilService,
        private timeService: TimeService,
        private service: SnapshotService,
        private buttonService: ButtonService,
        private lazyLoad: LazyloadService,
        private protectLunService: ProtectLunService,
    ) {
    }

    /**
     * 初始化
     */
    ngOnInit(): void {
        this.initVm();
        this.initTable();
        this.initBtn();
        this.initDataCard();

        this.initSelectLun();

        this.getBatchScheduleRest = this.scheduleService.getBatchRest(ScheduleTypeE.Block, SystemVstoreId);

        this.protectTypeChange(this.vmValue.protectType);
    }

    /**
     * 初始化选择LUN
     */
    initSelectLun(): void {
        // 在未传入已选LUN时默认展开推拉表格
        this.lunTableExpand = this.inputLuns.length === 0;

        if (this.inputLuns) {
            this.selectedLuns = this.inputLuns;

            // 若传入单个数据，则获取对应目标LUN Rest
            if (this.selectedLuns.length === 1) {
                this.targetLunRest = this.service.getTargetLunRest(this.inputLuns[0].ID, this.selectedLuns[0].vstoreId);
            }
        }

        // 过滤可创建快照的lun。极简模式由于还可以加策略，所有这里就不做过滤了。
        if (!SWITCH.IS_SIMPLE_MODE) {
            this.protectLunService.getAvailableLuns(this.i18n.createTitle, this.lunRest,
                this.selectedLuns, this.selectType === SnapSourceSelectTypeE.Single)
                .subscribe((luns: Lun[]) => {
                    this.selectedLuns = luns;

                    this.checkSnapNameRange();
                    this.pageReady = true;
                    this.show = true;
                });
        }
    }

    /**
     * 表单初始化
     */
    initVm(): void {
        this.placeholderName = this.i18n.placeholderName255;
        this.vm = new FormGroup({
            protectType: new FormControl(this.showSchedule ? SnapProtectTypeE.CdpSchedule : SnapProtectTypeE.Snap),
            createType: new FormControl(SnapshotCreateModeE.newCreate),
            selectTargetLUN: new FormControl(null, {
                validators: [
                    Validators.required,
                ],
                updateOn: 'blur',
            }),
            name: new FormControl('', {
                validators: [
                    DMValidators.rangeByteLength(SnapCreateNameLenE.Min, SnapCreateNameLenE.SingleMax),
                    DMValidators.commonName,
                ],
                updateOn: 'blur',
            }),
            des: new FormControl('', {
                validators: [DMValidators.rangeByteLength(SnapCreateDesLenE.Min, SnapCreateDesLenE.Max)],
                updateOn: 'blur',
            }),
            schedule: new FormControl(null, {
                validators: [
                    Validators.required,
                ],
            }),
        });

        this.errorMessage = {
            selectTargetLUN: {
                required: this.str.requiredErr(this.i18n.targetLunLabel),
            },
            name: {
                rangeByteLength: this.str.rangeByteLengthErr(SnapCreateNameLenE.Min, SnapCreateNameLenE.SingleMax),
                commonName: this.str.commonNameErr(this.i18n.name),
            },
            des: {
                rangeByteLength: this.str.rangeByteLengthErr(SnapCreateDesLenE.Min, SnapCreateDesLenE.Max),
            },
            schedule: {
                required: this.str.requiredErr(this.i18n.scheduleLabel),
            },
        };

        this.protectTypeChange(this.vmValue.protectType);
    }

    /**
     * 策略卡片初始化
     */
    initDataCard(): void {
        this.dataCardConfig = {
            title: {
                field: 'NAME',
            },
            tip: {
                render: (tip: string, config: CardTitleConfig, data: HyperCdpSchedule): string =>
                    this.scheduleService.renderStrategy(data, true).join('; '),
            },
            rows: 12,
            pageStyle: 'default',
        };

        // 填入默认策略，没有策略的情况下需要DM下发创建
        this.scheduleDefaultData = [SIMPLE_CDP_SCHEDULE_SAN_DEFAULT];

        if (this.modifySchedule) {
            this.initSelectVal = this.inputLuns[0].hyperCdpScheduleId;
        }

    }

    /**
     * 表格初始化
     */
    initTable(): void {
        this.columnDefs = [
            {
                field: 'NAME',
                header: this.i18n.name,
                filterType: 'search',
                sortable: true,
            },
            {
                field: 'WWN',
                header: this.i18n.wwn,
                filterType: 'search',
                columnFilter: true,
                render: (value: string) => value ?? UNPLACEHOLDER,
                hidden: true,
            },
            {
                field: 'vstoreName',
                header: this.i18n.vstoreNameLab,
                columnFilter: true,
                ignore: this.isSimpleMode || (this.vstore && this.vstore.value),
                render: (value: string) => value || UNPLACEHOLDER,
            },
            {
                field: 'vstoreId',
                header: this.i18n.vstoreIdLab,
                columnFilter: true,
                hidden: true,
                ignore: this.isSimpleMode || (this.vstore && this.vstore.value),
                render: (value: string) => value || UNPLACEHOLDER,
            },
            {
                field: 'CAPACITY',
                header: this.i18n.capacity,
                sortable: true,
                align: 'right',
                columnFilter: true,
                compare: (a: number, b: number) => a - b,
                render: (value: number) => this.utilService.adaptUnit(value, CAPACITY_UNIT.SECTOR).label,
            },
        ];

        this.tableConfig = {
            dataKey: 'ID',
        };

        // 批量查询LUN Rest
        // 1、只有快照选项，可以筛选RSSTYPE 22可以作为快照源LUN的LUN。（标准模式 or 极简模式且只有快照权限场景）
        // 2、只有CDP选项，创建入口隐藏，无场景
        // 3、同时有快照和CDP选项，不能用RSSTYPE筛选，需要做LUN类型过滤
        if (!this.showSchedule && this.showSnap) {
            this.lunRest = this.service.getLunRestSnapOnly();
        } else {
            this.lunRest = this.service.getLunRestSnapAndSchedule();
        }

        // 创建快照高危提示中，目标LUN表格列配置
        this.targetLunColumnDefs = [
            {
                field: 'label',
                header: this.i18n.targetLunLabel,
            },
            {
                field: 'value',
                header: this.i18n.targetLunIdLab,
            },
        ];

        // 修改策略高危提示中，成员LUN表格列配置
        this.modifyScheduleTableColumnDefs = [
            {
                field: 'NAME',
                header: this.scheduleStrService.table.nameLab,
            },
            {
                field: 'ID',
                header: this.scheduleStrService.table.idLab,
            },
        ];
    }

    /**
     * 推拉表格lun选择项变化
     */
    lunSelectionChange(): void {
        this.selectedLunsChanged = true;
        this.selectLunValid = !this.selectedLuns.length;

        // 若选中LUN个数不为1则创建模式切换为新建快照,为1则获取对应目标LUN Rest
        if (this.selectedLuns.length !== 1) {
            this.createTypeChanged(SnapshotCreateModeE.newCreate);
            this.vm.get('name').patchValue('');
        } else {
            this.targetLunRest = this.service.getTargetLunRest(this.selectedLuns[0].ID, this.selectedLuns[0].vstoreId);
            this.selectedTargetLUN = null;
        }

        this.checkSnapNameRange();
    }

    /**
     * 创建模式改变监听
     */
    createTypeChanged(value: SnapshotCreateModeE): void {
        this.vm.get('createType').patchValue(value);

        if (value === SnapshotCreateModeE.newCreate) {
            // 新创建
            this.vm.get('selectTargetLUN').disable();

            this.vm.get('name').patchValue('');
        } else {
            // 选择目标LUN
            this.vm.get('selectTargetLUN').enable();

            const selectTargetLunControl = this.vm.get('selectTargetLUN') as FormControl;
            selectTargetLunControl.clearError();

            if (this.selectedTargetLUN) {
                this.vm.get('name').patchValue(this.selectedTargetLUN.label);
            }
        }
    }

    /**
     * 目标LUN下拉框改变监听
     */
    targetLunSelectChange({ option }): void {
        if (option) {
            this.selectedTargetLUN = { ...option };
            if (this.vm.get('createType').value !== SnapshotCreateModeE.newCreate) {
                this.vm.get('name').patchValue(option.label);
            }
        }
    }

    /**
     * 保护类型改变。仅极简模式支持，可以选加入CDP计划
     * @param value
     */
    protectTypeChange(value: SnapProtectTypeE): void {
        if (value === SnapProtectTypeE.CdpSchedule) {
            this.vm.get('schedule').enable();

            this.vm.get('name').disable();
            this.vm.get('des').disable();
            this.vm.get('selectTargetLUN').disable();
        } else {
            this.vm.get('schedule').disable();

            this.vm.get('name').enable();
            this.vm.get('des').enable();

            // 快照场景，触发一下创建类型改变，避免selectTargetLUN表单失效
            this.createTypeChanged(this.vmValue.createType);
        }
    }

    /**
     * 名称范围，与多选单选相关
     */
    checkSnapNameRange(): void {
        let length: number;

        if (this.selectedLuns.length > 1) {
            // 选择了多个LUN，验证长度改为0-251
            length = SnapCreateNameLenE.MultiMax;
            this.placeholderName = this.i18n.placeholderName251;
        } else {
            // 单个LUN，验证长度改为0-255
            length = SnapCreateNameLenE.SingleMax;
            this.placeholderName = this.i18n.placeholderName255;
        }

        this.vm.get('name').clearValidators();
        this.vm.get('name').setValidators([
            DMValidators.rangeByteLength(SnapCreateNameLenE.Min, length),
            DMValidators.commonName,
        ]);

        this.errorMessage.name.rangeByteLength = this.str.rangeByteLengthErr(SnapCreateNameLenE.Min, length);
        this.vm.validate();
    }

    /**
     * 自动生成单个快照名
     * @param LunName 源LUN名称
     * @param i 序号
     * @returns
     */
    getNameSingle(LunName: string, i: number): string {
        const len = (this.timestamp + i).length;
        return this.utilService.byteSubstring(LunName, SnapCreateNameLenE.SingleMax - len) + this.timestamp + i;
    }

    /**
     * 自动生成多个快照名
     * @param LunName 源LUN名称
     * @param i 序号
     * @returns
     */
    getNameMulti(LunName: string, i: number): string {
        const len = i.toString().length;
        let index = i.toString();
        for (let j = 0; j < 4 - len; j++) {
            index = '0' + index;
        }
        return LunName + index;
    }

    /**
     *  选中项是否有远程复制
     */
    hasRemoteReplicationLun(): void {
        const remoteReplicationLength = this.selectedLuns.filter((item) => Object.prototype.hasOwnProperty.call(item, 'HASRSSOBJECT'));
        // 判断选择LUN是否含有远程复制
        if (remoteReplicationLength.length) {
            this.hasRemoteReplication = remoteReplicationLength.some((item) => JSON.parse(item.HASRSSOBJECT).RemoteReplication === 'TRUE');
        } else {
            this.hasRemoteReplication = false;
        }
    }

    /**
     * 初始化按钮
     */
    initBtn(): void {
        this.scheduleBtnGroup = [this.buttonService.create({
            id: 'hyper-cdp-schedule-main-create-button',
            theme: 'primary',
            label: this.i18n.addNewScheduleBtn,
            permission: ['@hyper_cdp_W'],
            license: [LicenseKey.HyperCDP],
            command: () => {
                this.lazyLoad.appendComponent(CreateComponent, {
                    type: ScheduleTypeE.Block,
                    vstore: {
                        label: SYSTEM_VSTORE_NAME,
                        value: SystemVstoreId,
                    },
                    onClose: (createObj: HyperCdpSchedule) => {
                        if (!createObj) {
                            return;
                        }

                        this.scheduleDataCard?.autoSelectLastPageItemByRule({
                            key: 'ID',
                            value: createObj.ID,
                        });
                    },
                });
            },
        })];
    }

    /**
     * 获取将LUN加入定时计划的cmd命令
     * @returns
     */
    getAddCdpScheduleCmds(): Command[] {
        const cmds: Command[] = [];
        const needCreateSchedule = this.vmValue.schedule.ID === undefined;

        if (this.modifySchedule) {
            // 修改场景，只会传入一个LUN
            const oriScheduleId = this.inputLuns[0].hyperCdpScheduleId;

            // 先判断是否新选中的和老的一样，是的话直接关闭
            if (this.vmValue.schedule.ID === oriScheduleId) {
                return [];
            }

            // 先下发移除
            const removeHyperCDPScheduleCmd: Command = {
                id: 'removeHyperCDPSchedule',
                name: this.i18n.removeMemberLUN.replace('{0}', this.inputLuns[0].NAME)
                    .replace('{1}', (this.inputLuns[0] as ProtectionLun).hyperCdpScheduleName),
                action: this.scheduleService.removeAssociate(oriScheduleId, this.inputLuns[0].ID,
                    MO.LUN, { doException: false }, SystemVstoreId),
            };
            cmds.push(removeHyperCDPScheduleCmd);
        }

        // 如果是DM预置策略，需要先下发创建
        if (needCreateSchedule) {
            const createHyperCDPScheduleCmd: Command = {
                id: 'createHyperCDPSchedule',
                name: this.i18n.createScheduleName(this.vmValue.schedule.NAME),
                action: this.scheduleService.create(
                    this.getDefaultScheduleParams(),
                    SystemVstoreId,
                    { doException: false },
                ),
            };
            cmds.push(createHyperCDPScheduleCmd);
        }

        // 将LUN添加到CDP策略中
        this.selectedLuns.forEach((lun) => {
            cmds.push({
                id: `addLunToSchedule_${lun.NAME}`,
                deps: needCreateSchedule ? ['createHyperCDPSchedule'] : undefined,
                name: this.i18n.addLunResultName(lun.NAME, this.vmValue.schedule.NAME),
                action: (res: JobResponse) => {
                    const createdSchedule = res && res.data as HyperCdpSchedule;
                    const scheduleId = (needCreateSchedule && createdSchedule) ? createdSchedule.ID : this.vmValue.schedule.ID;

                    return this.scheduleService.addAssociateMember(scheduleId, lun.ID, MO.LUN, { doException: false }, lun.vstoreId);
                },
            });
        });

        // DM自动创建的策略需要立即启动
        if (needCreateSchedule) {
            cmds.push({
                id: 'activate',
                deps: ['createHyperCDPSchedule'],
                name: this.i18n.activateCdpScheduleMsg.replace('{0}', this.vmValue.schedule.NAME),
                action: (res: JobResponse) => {
                    const createdSchedule = res && res.data as HyperCdpSchedule;

                    return this.scheduleService.activate(
                        createdSchedule.ID,
                        undefined,
                        { doException: false },
                    );
                },
            });
        }

        return cmds;
    }

    /**
     * 获取DM预置LUN定时计划下发参数
     * @returns
     */
    getDefaultScheduleParams(): HyperCdpSchedule {
        const defaultSchedule = SIMPLE_CDP_SCHEDULE_SAN_DEFAULT;

        const params: HyperCdpSchedule = {
            NAME: defaultSchedule.NAME,
            FREQUENCYVALUE: defaultSchedule.FREQUENCYVALUE,
            FREQUENCYNUM: defaultSchedule.FREQUENCYNUM,
            DAYHOURS: JSON.parse(defaultSchedule.DAYHOURS),
            DAYMINUTE: defaultSchedule.DAYMINUTE,
            DAILYSNAPSHOTNUM: defaultSchedule.DAILYSNAPSHOTNUM,
            WEEKLYDAYS: JSON.parse(defaultSchedule.WEEKLYDAYS),
            STARTTIMEOFWEEK: defaultSchedule.STARTTIMEOFWEEK,
            WEEKLYSNAPSHOTNUM: defaultSchedule.WEEKLYSNAPSHOTNUM,
            SECURESNAPENABLED: this.utilService.toBool(defaultSchedule.SECURESNAPENABLED),
            OBJECTTYPE: objectTypeE.block,
            vstoreId: SystemVstoreId,
        };

        return params;
    }

    /**
     * 获取创建快照的cmd命令
     * @returns
     */
    getCrateSnapCmds(): Command[] {
        let name: string;
        let batchParams: SnapshotSendType[] = [];
        let batchItemNames: SnapshotName[] = [];
        const cmds: Command[] = [];

        this.selectedLuns.forEach((element: Lun, index: number) => {
            // 若不输入名称，则快照名称为LUN + 时间戳 + 序号
            if (this.vm.get('name').value === '') {
                name = this.getNameSingle(element.NAME, index);
            }

            // 若输入名称，选择单个LUN，名称则为所输入的名称(255)
            if (this.vm.get('name').value !== '' && this.selectedLuns.length === 1) {
                name = this.vm.get('name').value;
            }

            // 若输入名称，选择多个LUN，名称则为所输入的名称(251) + 四位序号
            if (this.vm.get('name').value !== '' && this.selectedLuns.length !== 1) {
                name = this.getNameMulti(this.vm.get('name').value, index);
            }

            const body: SnapshotSendType = {
                NAME: name,
                DESCRIPTION: this.vmValue.des,
                PARENTID: element.ID,
                PARENTTYPE: element.TYPE,
            };

            // 判断是否有远程复制
            this.hasRemoteReplicationLun();

            // 目标LUN创建快照，通过原rest接口创建
            if (this.vm.get('createType').value === SnapshotCreateModeE.targetObjCreate) {
                body.dstLunId = this.vm.get('selectTargetLUN').value;
                const rest = this.service.createSnapshotRest();

                cmds.push({
                    name: this.i18n.createSnapshotRest.replace('{0}', name),
                    action: this.service.createSnapshot(rest, body, { restNameParams: [name] }),
                });
            }

            if (this.vm.get('createType').value === SnapshotCreateModeE.newCreate) {
                if (this.hasRemoteReplication) {
                    // 只要含有远程复制的LUN，走原来单个接口
                    const rest = this.service.createSnapshotRest();
                    const opt = {
                        restNameParams: [name],
                        doException: false,
                    };

                    cmds.push({
                        name: this.i18n.createSnapshotRest.replace('{0}', name),
                        action: this.service.createSnapshot(rest, body, opt),
                    });
                } else {
                    // 普通创建快照，通过秒级批量接口创建
                    batchParams.push(body);
                    batchItemNames.push({ name: this.i18n.createSnapshotRest.replace('{0}', name) });

                    if (batchItemNames.length === 100 || index === this.selectedLuns.length - 1) {
                        cmds.push({
                            name: batchItemNames[0].name,
                            action: this.service.createSnapshotBatch(batchParams, { doException: false }),
                            batchItemNames,
                        });

                        batchItemNames = [];
                        batchParams = [];
                    }
                }
            }
        });

        return cmds;
    }

    /**
     * 表单校验
     * @returns validate
     */
    validate(): Observable<boolean> {
        this.selectLunValid = !this.selectedLuns.length;
        if (this.selectedLuns.length === 0) {
            return of(false);
        }

        return this.vm.validateOb();
    }

    /**
     * 获取高危提示
     * @returns
     */
    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];

        // 修改策略，会下发先移除再添加，会删除CDP，需要进行提示
        if (this.modifySchedule) {
            // 修改场景，只会传入一个LUN
            const oriScheduleId = this.inputLuns[0].hyperCdpScheduleId;
            if (this.vmValue.schedule.ID === oriScheduleId) {
                return [];
            }

            prompts.push({
                id: 'prompt-lun-modify-schedule',
                opType: 'warning',
                title: this.scheduleStrService.title.modifySchedule,
                content: this.scheduleStrService.removeHyperCDPScheduleMember((this.inputLuns[0] as ProtectionLun).hyperCdpScheduleName),
                templateRef: this.modifyScheduleTableTemplate,
                canHidden: true,
                width: 700,
            });
        }

        // 单次快照，且选择目标LUN场景才需要高危提示
        if (this.vmValue.protectType === SnapProtectTypeE.Snap && this.vmValue.createType === SnapshotCreateModeE.targetObjCreate) {
            prompts.push({
                id: 'prompt-lun-snapshot-create-onSave',
                opType: 'warning',
                title: this.i18n.createSnapshotPromptTitle,
                content: this.i18n.createSnapshotPromptMsg,
                templateRef: this.targetLunTemplate,
                canHidden: true,
                width: 700,
            });
        }

        return prompts;
    }

    /**
     * 获取下发的cmd
     */
    buildCmds(): Command[] {
        let cmds: Command[] = [];

        if (this.vmValue.protectType === SnapProtectTypeE.CdpSchedule) {
            cmds = this.getAddCdpScheduleCmds();
        } else {
            cmds = this.getCrateSnapCmds();
        }

        return cmds;
    }
}
